import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Printer, ArrowLeft } from "lucide-react";

function fmt(n) {
    return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
        return iso;
    }
}

export default function Invoice() {
    const { orderId } = useParams();
    const [inv, setInv] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/billing/invoice/${orderId}`)
            .then(({ data }) => setInv(data))
            .catch((err) => setError(err.response?.data?.detail || "Could not load invoice"));
    }, [orderId]);

    const handlePrint = () => window.print();

    if (error) {
        return (
            <div className="min-h-screen bg-white text-zinc-900 flex items-center justify-center px-6">
                <div className="text-center">
                    <p className="text-red-600 font-medium mb-4">{error}</p>
                    <Link to="/billing" className="text-zinc-700 underline">Back to billing</Link>
                </div>
            </div>
        );
    }
    if (!inv) {
        return (
            <div className="min-h-screen bg-white text-zinc-900 flex items-center justify-center">
                <span className="font-mono text-sm text-zinc-500">Loading invoice...</span>
            </div>
        );
    }

    const isTaxInvoice = inv.tax?.has_gst;
    const documentTitle = isTaxInvoice ? "Tax Invoice" : "Payment Receipt";

    return (
        <div className="min-h-screen bg-neutral-100 py-8 print:bg-white print:py-0" data-testid="invoice-page">
            {/* Toolbar — hidden on print */}
            <div className="max-w-3xl mx-auto px-6 mb-4 flex items-center justify-between print:hidden">
                <Link to="/billing" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900" data-testid="invoice-back">
                    <ArrowLeft className="w-4 h-4" /> Back to billing
                </Link>
                <button
                    onClick={handlePrint}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700"
                    data-testid="invoice-print"
                >
                    <Printer className="w-4 h-4" /> Print / Save as PDF
                </button>
            </div>

            {/* Invoice sheet — A4-friendly */}
            <div
                className="max-w-3xl mx-auto bg-white text-zinc-900 shadow-lg print:shadow-none border border-zinc-200 print:border-0"
                style={{ minHeight: "297mm", padding: "24mm 20mm" }}
                data-testid="invoice-sheet"
            >
                {/* Header */}
                <div className="flex items-start justify-between pb-6 border-b-2 border-zinc-900">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">{documentTitle}</div>
                        <h1 className="text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {inv.seller.name}
                        </h1>
                        {inv.seller.address && <p className="text-sm text-zinc-700 mt-1 whitespace-pre-line max-w-xs">{inv.seller.address}</p>}
                        {inv.seller.email && <p className="text-sm text-zinc-700 mt-0.5">{inv.seller.email}</p>}
                        <div className="mt-2 text-xs text-zinc-600 space-y-0.5">
                            {inv.seller.gstin && <div><span className="font-semibold">GSTIN:</span> {inv.seller.gstin}</div>}
                            {inv.seller.pan && <div><span className="font-semibold">PAN:</span> {inv.seller.pan}</div>}
                            {inv.seller.state && <div><span className="font-semibold">State:</span> {inv.seller.state}</div>}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Invoice number</div>
                        <div className="text-lg font-mono font-semibold text-zinc-900" data-testid="invoice-number">{inv.invoice_number || "—"}</div>
                        <div className="mt-3 text-xs font-mono uppercase tracking-widest text-zinc-500">Date</div>
                        <div className="text-sm text-zinc-800">{fmtDate(inv.invoice_date)}</div>
                    </div>
                </div>

                {/* Buyer + payment refs */}
                <div className="grid grid-cols-2 gap-8 mt-8">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Billed to</div>
                        <div className="text-base font-semibold text-zinc-900">{inv.buyer.name}</div>
                        <div className="text-sm text-zinc-700">{inv.buyer.email}</div>
                    </div>
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Payment</div>
                        <div className="text-sm text-zinc-800"><span className="font-semibold">Method:</span> {inv.payment_method}</div>
                        <div className="text-sm text-zinc-800 break-all"><span className="font-semibold">Order:</span> {inv.order_id}</div>
                        {inv.razorpay_payment_id && (
                            <div className="text-sm text-zinc-800 break-all"><span className="font-semibold">Payment ID:</span> {inv.razorpay_payment_id}</div>
                        )}
                        <div className="text-sm text-zinc-800 mt-1"><span className="font-semibold">Status:</span> <span className="text-green-700 font-semibold uppercase">{inv.status}</span></div>
                    </div>
                </div>

                {/* Line items */}
                <table className="w-full mt-8 text-sm">
                    <thead>
                        <tr className="border-b-2 border-zinc-900 text-left">
                            <th className="py-2 font-semibold">Description</th>
                            <th className="py-2 font-semibold text-center">HSN/SAC</th>
                            <th className="py-2 font-semibold text-right">Qty</th>
                            <th className="py-2 font-semibold text-right">Rate (₹)</th>
                            <th className="py-2 font-semibold text-right">Amount (₹)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inv.line_items.map((li) => (
                            <tr key={li.description} className="border-b border-zinc-200 align-top">
                                <td className="py-3 pr-2 text-zinc-800">{li.description}</td>
                                <td className="py-3 text-center text-zinc-700 font-mono text-xs">{li.hsn_sac}</td>
                                <td className="py-3 text-right text-zinc-700">{li.quantity}</td>
                                <td className="py-3 text-right text-zinc-700">{fmt(li.rate)}</td>
                                <td className="py-3 text-right text-zinc-900 font-medium">{fmt(li.amount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end mt-6">
                    <div className="w-72 text-sm">
                        {isTaxInvoice ? (
                            <>
                                <div className="flex justify-between py-1.5 text-zinc-700">
                                    <span>Subtotal</span>
                                    <span>₹{fmt(inv.tax.base_amount)}</span>
                                </div>
                                {inv.tax.cgst_amount > 0 && (
                                    <>
                                        <div className="flex justify-between py-1.5 text-zinc-700">
                                            <span>CGST @ {inv.tax.cgst_rate}%</span>
                                            <span>₹{fmt(inv.tax.cgst_amount)}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 text-zinc-700">
                                            <span>SGST @ {inv.tax.sgst_rate}%</span>
                                            <span>₹{fmt(inv.tax.sgst_amount)}</span>
                                        </div>
                                    </>
                                )}
                                {inv.tax.igst_amount > 0 && (
                                    <div className="flex justify-between py-1.5 text-zinc-700">
                                        <span>IGST @ {inv.tax.igst_rate}%</span>
                                        <span>₹{fmt(inv.tax.igst_amount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between py-2 mt-1 border-t-2 border-zinc-900 text-base font-bold text-zinc-900">
                                    <span>Total</span>
                                    <span>₹{fmt(inv.tax.total)}</span>
                                </div>
                            </>
                        ) : (
                            <div className="flex justify-between py-2 border-t-2 border-zinc-900 text-base font-bold text-zinc-900">
                                <span>Total paid</span>
                                <span>₹{fmt(inv.amount_paid)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Amount in words */}
                <div className="mt-6 p-3 bg-zinc-50 border-l-4 border-zinc-900 text-sm text-zinc-800">
                    <span className="font-semibold">Amount in words: </span>{inv.amount_in_words}
                </div>

                {/* Footer notes */}
                <div className="mt-10 pt-6 border-t border-zinc-200 text-xs text-zinc-600 space-y-1">
                    {!isTaxInvoice && (
                        <p className="italic">
                            This document is a payment receipt, not a tax invoice. GST has not been charged as the seller is not
                            currently registered under GST.
                        </p>
                    )}
                    {isTaxInvoice && (
                        <p className="italic">
                            This is a computer-generated tax invoice and does not require a signature.
                        </p>
                    )}
                    <p className="pt-2">
                        For any queries, contact <span className="font-semibold text-zinc-800">{inv.seller.email}</span>.
                    </p>
                </div>
            </div>

            <style>{`
                @media print {
                    @page { size: A4; margin: 0; }
                    body { background: white !important; }
                }
            `}</style>
        </div>
    );
}
